import Phaser from 'phaser';

import { ActionBus } from 'game-logic';
import { ChipButtonControlViewModel } from './chip-button-control-view-model';
import { BlackjackMainScene } from '../../../scenes/blackjack-main-scene';
import { ChipActions } from './chip-picker-view';
import { ResizeMetrics } from '../../../model/resize-metrics';
import { Subscription } from 'rxjs';
import { UxMetrics } from '../../../model/ux-metrics';

export class ChipButtonControlView extends Phaser.GameObjects.Container {
  private subscriptionGroup: Subscription;

  private rectangle: Phaser.GameObjects.Rectangle;
  private innerContainer: Phaser.GameObjects.Container;
  private button: Phaser.GameObjects.Image;
  private hasDragged = false;
  private startX = 0;
  private buttonAnimation: Phaser.Tweens.Tween;

  constructor(
    scene: BlackjackMainScene,
    x: number,
    y: number,
    private viewModel: ChipButtonControlViewModel,
    private chipActionBus: ActionBus<ChipActions>,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();

    this.rectangle = scene.add.rectangle(0, 0, 0, 0, 0x121212).setAlpha(0).setOrigin(0.5, 0.5);
    this.add(this.rectangle);

    this.innerContainer = scene.add.container(0, 0);
    this.add(this.innerContainer);

    this.button = scene.add.image(0, 0, viewModel.buttonAsset.type).setOrigin(0.5, 0.5);
    this.innerContainer.add(this.button);

    this.setName(ChipButtonControlView.name);
    scene.add.existing(this);

    this.handleInput();

    this.buttonAnimation = scene.tweens.add({
      targets: this.innerContainer,
      scaleX: { from: 0.95, to: 1.05 },
      scaleY: { from: 0.95, to: 1.05 },
      yoyo: true,
      repeat: -1, // Infinite
      duration: UxMetrics.ChipButtonAnimation,
      ease: Phaser.Math.Easing.Quadratic.InOut,
      hold: UxMetrics.ChipButtonHoldDelay,
    });

    this.subscriptionGroup.add(
      viewModel.startButtonAnimation$.subscribe((start) => {
        if (start) {
          this.buttonAnimation.seek(UxMetrics.ChipButtonAnimation * 0.5);
          this.buttonAnimation.play();
        } else {
          this.buttonAnimation.pause();

          scene.tweens.add({
            targets: this.innerContainer,
            scaleX: 1.0,
            scaleY: 1.0,
            duration: 150,
            ease: Phaser.Math.Easing.Quadratic.InOut,
          });
        }
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(fromScene);
  }

  public canDisplayForAmount(amount: number) {
    return this.viewModel.canDisplayForAmount(amount);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    const scalingMetrics = resizeMetrics.scalingMetrics;

    const buttonScale = 0.8;
    const width =
      this.viewModel.buttonAsset.dimensions.width * scalingMetrics.scaleFactor * buttonScale;
    const height =
      this.viewModel.buttonAsset.dimensions.height * scalingMetrics.scaleFactor * buttonScale;

    this.setSize(width, height);
    this.rectangle.setSize(width, height);
    this.innerContainer.setSize(width, height);

    const graphicsScale = 0.8;
    this.button.setDisplaySize(width * graphicsScale, height * graphicsScale);

    this.setInteractive({
      hitArea: this.rectangle,
      draggable: true,
      hitAreaCallback: (rect: Phaser.Geom.Rectangle, x: number, y: number) =>
        Phaser.Geom.Rectangle.Contains(rect, x, y),
    });
  }

  private handleInput(): void {
    this.on(
      Phaser.Input.Events.DRAG_START,
      (
        pointer: Phaser.Input.Pointer,
        dragX: number,
        dragY: number,
        _gameObject: Phaser.GameObjects.Image,
      ) => {
        this.chipActionBus.emit('onDragStart', { pointer, dragX, dragY });
        this.startX = pointer.x;
        this.hasDragged = false;
      },
    )
      .on(
        Phaser.Input.Events.DRAG,
        (pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
          this.chipActionBus.emit('onDrag', { pointer, dragX, dragY });
          const deltaX = Math.abs(pointer.x - this.startX);
          if (deltaX > 2) {
            // Drag by a small threshold
            this.hasDragged = true;
          }
        },
      )
      .on(
        Phaser.Input.Events.DRAG_END,
        (
          pointer: Phaser.Input.Pointer,
          dragX: number,
          dragY: number,
          _gameObject: Phaser.GameObjects.Image,
        ) => {
          this.chipActionBus.emit('onDragEnd', { pointer, dragX, dragY });
        },
      )
      .on(Phaser.Input.Events.POINTER_DOWN, (_pointer: Phaser.Input.Pointer) => {
        // Nothing
      })
      .on(Phaser.Input.Events.POINTER_UP, (_pointer: Phaser.Input.Pointer) => {
        if (!this.hasDragged) {
          this.viewModel.buttonClicked();
        }
        this.hasDragged = false;
      });
  }
}
